// frontend/src/components/chatbot/FollowUpButtons.tsx
import React, { useMemo } from 'react';
import useChatbot from './useChatbot';

interface FollowUpButtonsProps {
  query?: string;
  answer?: string;
}

interface Topic {
  keywords: string[];
  suggestions: string[];
}

const TOPICS: Topic[] = [
  {
    keywords: ['collection', 'bgg', 'import', 'sync', 'my games', 'library', 'wishlist', 'owned'],
    suggestions: [
      'How do I add a game to my Library?',
      'How do I filter my collection?',
      'How do I share my Library with others?',
    ],
  },
  {
    keywords: ['session', 'play', 'score', 'game night', 'host', 'match', 'timeline', 'log'],
    suggestions: [
      'How do I log a play session?',
      'What is the Timeline page?',
      'How do I switch between chart views on the Timeline?',
    ],
  },
  {
    keywords: ['search', 'filter', 'find', 'browse', 'discover', 'recommend', 'similar'],
    suggestions: [
      'How do I search for a game?',
      'How do I find games similar to one I like?',
      'What is Jet Set Go?',
    ],
  },
  {
    keywords: ['game', 'games', 'title', 'rating', 'rate', 'review'],
    suggestions: [
      'How do I add a game to my Library?',
      'How do I search for a game?',
      'How do I find games similar to one I like?',
    ],
  },
  {
    keywords: ['marketplace', 'price', 'buy', 'sell', 'trade', 'listing', 'watch', 'copy'],
    suggestions: [
      'How do I find a copy of a game to buy?',
      'Where can I find prices for a game?',
      'How do I switch between Marketplace view tabs?',
    ],
  },
  {
    keywords: ['profile', 'account', 'username', 'email', 'password', 'country', 'login', 'register', 'sign up', 'sign in'],
    suggestions: [
      'How do I create an account?',
      'How do I log in?',
      'I forgot my password. How do I reset it?',
    ],
  },
  {
    keywords: ['invite', 'friend', 'share', 'group', 'guest', 'survey', 'chart'],
    suggestions: [
      'How do I share my Library with others?',
      'How do I view a shared chart?',
      'How do I complete a survey?',
    ],
  },
  {
    keywords: ['rulebook', 'rules', 'how to play', 'manual', 'pdf'],
    suggestions: [
      'How do I find a copy of a game to buy?',
      'How do I search for a game?',
      'How do I find games similar to one I like?',
    ],
  },
  {
    keywords: ['notification', 'push', 'reminder', 'alert'],
    suggestions: [
      'How do I switch between light and dark mode?',
      'How do I log out?',
      'How do I search for a game?',
    ],
  },
  {
    keywords: ['settings', 'delete account', 'privacy', 'data', 'dark mode', 'logout', 'log out'],
    suggestions: [
      'How do I switch between light and dark mode?',
      'How do I log out?',
      'Can I use Pista without logging in?',
    ],
  },
];

const GENERIC_SUGGESTIONS: string[] = [
  'How do I search for a game?',
  'How do I log a play session?',
  'How do I add a game to my Library?',
];

const getSuggestions = (query = '', answer = ''): string[] => {
  const text = `${query} ${answer}`.toLowerCase();
  let bestMatch: Topic | null = null;
  let bestScore = 0;

  TOPICS.forEach((topic) => {
    const score = topic.keywords.reduce((acc, keyword) => {
      if (text.includes(keyword.toLowerCase())) return acc + 1;
      return acc;
    }, 0);
    if (score > bestScore) {
      bestScore = score;
      bestMatch = topic;
    }
  });

  if (bestMatch && bestScore > 0) {
    return bestMatch.suggestions.slice(0, 3);
  }

  return GENERIC_SUGGESTIONS;
};

const FollowUpButtons: React.FC<FollowUpButtonsProps> = ({ query = '', answer = '' }) => {
  const { sendMessage } = useChatbot();
  const suggestions = useMemo(() => getSuggestions(query, answer), [query, answer]);

  if (!suggestions.length) return null;

  return (
    <div className="chatbot-followups" aria-label="Suggested follow-up questions">
      {suggestions.map((suggestion, index) => (
        <button
          key={`followup-${index}`}
          type="button"
          className="chatbot-followup-btn"
          onClick={() => sendMessage(suggestion)}
        >
          {suggestion}
        </button>
      ))}
    </div>
  );
};

export default FollowUpButtons;
